/**
 * Shared form-search context (FEAT-2026-0005, Phase 5).
 *
 * The Form pane has 30+ fields across 5–6 accordion sections. Finding "the
 * Flow Curve's marker radius" without scrolling and expanding/collapsing
 * three times is painful. The search box at the top of FormPane writes a
 * query into this context; ObjectFieldTemplate (root) hides accordion
 * sections that don't match either by name, by SECTION_TITLES / blurb, or
 * by any of their nested field labels — so a non-empty query collapses the
 * form down to "just the bits that contain X."
 *
 * Why context, not the global zundo store: search is purely UI state and
 * doesn't belong on the timeline. Living in context also keeps it scoped
 * to the form pane so it doesn't survive a navigation away.
 */

import { createContext, useContext } from 'react';

export interface FormSearchState {
  /** Lower-cased trimmed query. Empty string means "show everything." */
  query: string;
}

export const FormSearchContext = createContext<FormSearchState>({ query: '' });

export const useFormSearch = (): FormSearchState => useContext(FormSearchContext);

/**
 * Substring match used by ObjectFieldTemplate. Lower-cases both sides, treats
 * an empty query as "always match" so the consumer can call this
 * unconditionally without first guarding on `query.length`.
 */
export function matchesQuery(haystack: string | undefined | null, query: string): boolean {
  if (!query) return true;
  if (!haystack) return false;
  return haystack.toLowerCase().includes(query);
}

/**
 * Phase 8 form-density polish: ArrayFieldTemplate sets this to `true`
 * when it's rendering an array of primitives (numbers, strings) in a
 * compact horizontal layout. FieldTemplate reads it and skips the
 * per-item label ("xTickPositions-3") that would otherwise stack each
 * item vertically — letting the items sit cleanly inline at ~70 px wide.
 */
export const PrimitiveArrayItemContext = createContext<boolean>(false);

